import React, { useEffect, useState } from 'react';
import { FileText } from 'lucide-react';
import { taxInvoicesService, TaxDocumentView } from '../../api/services/taxInvoices.service';
import { formatMoney } from '../../types/billing';

const card = 'bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-4';
const h2 = 'text-lg font-semibold text-gray-900 dark:text-white';
const muted = 'text-sm text-gray-500 dark:text-gray-400';
const th = 'px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase whitespace-nowrap text-left';
const td = 'px-3 py-2 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap';
const tdNum = `${td} text-right tabular-nums`;

export const SUPPLY_LABEL: Record<TaxDocumentView['supplyType'], string> = {
  INTRA_STATE: 'CGST + SGST',
  INTER_STATE: 'IGST',
  EXPORT_LUT: 'Export (LUT)',
  SEZ_LUT: 'SEZ (LUT)',
};

/** Tax of a document as one figure: CGST + SGST + IGST. */
export const taxOf = (d: Pick<TaxDocumentView, 'cgstAmount' | 'sgstAmount' | 'igstAmount'>): number =>
  Number(d.cgstAmount || 0) + Number(d.sgstAmount || 0) + Number(d.igstAmount || 0);

/**
 * GST tax invoices and credit notes issued by or to this organisation, with PDF download.
 * The server decides what the caller may see (the recipient never sees drafts). Renders
 * NOTHING while the engine is off (the API answers 404), so the page is unchanged.
 */
const TaxInvoicesPanel: React.FC<{ organizationId: string }> = ({ organizationId }) => {
  const [docs, setDocs] = useState<TaxDocumentView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let live = true;
    setDocs(null);
    setError(null);
    setHidden(false);
    taxInvoicesService.list({ organizationId, page: 0, size: 50 }).then((r) => {
      if (!live) return;
      if (r.success) setDocs(r.data.content);
      else if (r.disabled) setHidden(true);
      else setError(r.error);
    });
    return () => {
      live = false;
    };
  }, [organizationId]);

  if (hidden) return null;

  const download = async (d: TaxDocumentView) => {
    const r = await taxInvoicesService.downloadPdf(d);
    if (!r.ok) setError(r.error || 'Download failed');
  };

  return (
    <section className={card} aria-label="Tax invoices">
      <div>
        <h2 className={h2}>GST tax invoices</h2>
        <p className={muted}>Tax invoices and credit notes issued by or to this organisation. Issued documents never change.</p>
      </div>
      {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
      {docs === null && !error && <p className={muted}>Loading…</p>}
      {docs !== null && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th className={th}>Number</th>
                <th className={th}>Date</th>
                <th className={th}>From → To</th>
                <th className={th}>Tax</th>
                <th className={`${th} text-right`}>Taxable</th>
                <th className={`${th} text-right`}>GST</th>
                <th className={`${th} text-right`}>Total</th>
                <th className={th}>Status</th>
                <th className={th} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {docs.length === 0 && (
                <tr><td className={td} colSpan={9}>No tax invoices yet</td></tr>
              )}
              {docs.map((d) => (
                <tr key={d.id}>
                  <td className={td}>
                    {d.documentNumber || 'Draft'}
                    {d.documentType === 'CREDIT_NOTE' ? ' (credit note)' : ''}
                  </td>
                  <td className={td}>{d.issueDate || '—'}</td>
                  <td className={td}>{d.issuerName || d.issuerOrganizationId} → {d.recipientName || d.recipientOrganizationId}</td>
                  <td className={td}>{SUPPLY_LABEL[d.supplyType] || d.supplyType}</td>
                  <td className={tdNum}>{formatMoney(d.taxableValue, d.currency)}</td>
                  <td className={tdNum}>{formatMoney(taxOf(d), d.currency)}</td>
                  <td className={tdNum}>{formatMoney(d.totalAmount, d.currency)}</td>
                  <td className={td}>{d.status}</td>
                  <td className={td}>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                      onClick={() => download(d)}
                      aria-label={`Download ${d.documentNumber || 'draft'} PDF`}
                    >
                      <FileText className="w-4 h-4" /> PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default TaxInvoicesPanel;
